/**
 * Ownership: Mesa ground + road ribbons. No actors.
 * Talks via: terrain + roads. View adds the meshes.
 * Budget: keep this file under ~300 lines.
 */

import * as THREE from 'three';
import { roadWidth, sampleElevGrid, type RoadPoly } from './geo';
import type { JanSlice } from './slice';

const SAND = 0xc4a574;
const ASPHALT = 0x3f3f48;

export type GroundWorld = Pick<JanSlice, 'terrain'> & {
  nearbyRoads?: RoadPoly[];
  roads?: RoadPoly[];
};

function ribbonRoads(world: GroundWorld): RoadPoly[] {
  return world.roads ?? world.nearbyRoads ?? [];
}

function paintRoads(world: GroundWorld): RoadPoly[] {
  return world.nearbyRoads ?? [];
}

export function buildGround(slice: GroundWorld): THREE.Mesh {
  const half = Math.min(900, slice.terrain.map.halfExtentM || 140);
  const segs = half > 200 ? 80 : 56;
  const geo = new THREE.PlaneGeometry(half * 2, half * 2, segs, segs);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const sand = new THREE.Color(SAND);
  const rock = new THREE.Color(0x8b7355);
  const ash = new THREE.Color(ASPHALT);
  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const y = sampleElevGrid(x, z, slice.terrain.map);
    pos.setY(i, y);
    const near = nearestRoadDist(x, z, paintRoads(slice));
    const c = sand.clone().lerp(rock, 0.2);
    if (near && near.dist < near.halfW) {
      c.copy(ash);
    } else if (near && near.dist < near.halfW + 3) {
      c.lerp(ash, 0.35);
    }
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  pos.needsUpdate = true;
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  return new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.94,
    }),
  );
}

export function buildRoads(slice: GroundWorld): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: ASPHALT,
    roughness: 0.82,
    side: THREE.DoubleSide,
  });
  for (const road of ribbonRoads(slice)) {
    const mesh = roadRibbon(road.points, roadWidth(road.highway), slice);
    if (mesh) {
      mesh.material = mat;
      g.add(mesh);
    }
  }
  return g;
}

function nearestRoadDist(
  x: number,
  z: number,
  roads: readonly RoadPoly[],
): { dist: number; halfW: number } | null {
  let best: { dist: number; halfW: number } | null = null;
  for (const road of roads) {
    const halfW = roadWidth(road.highway) * 0.5;
    for (let i = 1; i < road.points.length; i += 1) {
      const [x0, z0] = road.points[i - 1];
      const [x1, z1] = road.points[i];
      const d = pointSegDist(x, z, x0, z0, x1, z1);
      if (!best || d < best.dist) {
        best = { dist: d, halfW };
      }
    }
  }
  return best;
}

function pointSegDist(
  x: number,
  z: number,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
): number {
  const dx = x1 - x0;
  const dz = z1 - z0;
  const len2 = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((x - x0) * dx + (z - z0) * dz) / len2));
  return Math.hypot(x - (x0 + dx * t), z - (z0 + dz * t));
}

function roadRibbon(
  points: number[][],
  width: number,
  slice: Pick<GroundWorld, 'terrain'>,
): THREE.Mesh | null {
  if (points.length < 2) {
    return null;
  }
  const halfW = width * 0.5;
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i += 1) {
    const [x, z] = points[i];
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    const tx = next[0] - prev[0];
    const tz = next[1] - prev[1];
    const len = Math.hypot(tx, tz) || 1;
    const lx = -tz / len;
    const lz = tx / len;
    for (const side of [-1, 1]) {
      const rx = x + lx * halfW * side;
      const rz = z + lz * halfW * side;
      const y =
        Math.max(
          slice.terrain.sampleHeight(x, z),
          slice.terrain.sampleHeight(rx, rz),
        ) + 0.12;
      positions.push(rx, y, rz);
    }
  }
  for (let i = 0; i < points.length - 1; i += 1) {
    const L0 = i * 2;
    indices.push(L0, L0 + 2, L0 + 1, L0 + 1, L0 + 2, L0 + 3);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return new THREE.Mesh(geo, new THREE.MeshStandardMaterial());
}
